import React, { useEffect, useState } from "react";
import ToolShell from "./ToolShell";
import { Route as RouteIcon, ArrowRightLeft } from "lucide-react";
import { fetchCities, fetchDistance } from "../../lib/api";
import { useLocale } from "../../i18n";

export default function DistanceTool() {
  const { t, locale } = useLocale();
  const [cities, setCities] = useState([]);
  const [from, setFrom] = useState("Bucharest");
  const [to, setTo] = useState("Brașov");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchCities().then(setCities).catch(() => {}); }, []);

  const run = async () => {
    setLoading(true);
    try {
      const d = await fetchDistance(from, to);
      setResult(d);
    } catch (e) { setResult({ error: true }); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (cities.length) run(); /* eslint-disable-next-line */ }, [cities]);

  return (
    <ToolShell
      title={t("tools.distance")}
      subtitle={t("tools.distance_sub")}
      icon={RouteIcon}
      testid="tool-distance"
      seoPath="/tools/distance"
      seoTitle={locale === "ro" ? "Calculator distanțe România" : "Romania distance calculator"}
      seoDescription={locale === "ro" ? "Calculează distanța rutieră între orașele românești, cu timp de condus și cost combustibil estimat." : "Distance between Romanian cities with drive-time and fuel-cost estimates."}
    >
      <div className="grid lg:grid-cols-12 gap-6">
        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="lg:col-span-5 wr-sharp-card p-8 space-y-6" data-testid="distance-form">
          <div>
            <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.from")}</label>
            <select value={from} onChange={(e) => setFrom(e.target.value)} className="w-full font-mono-wr text-lg border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2 focus:border-[var(--wr-ink)] focus:outline-none" data-testid="distance-from">
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex justify-center">
            <button type="button" onClick={() => { setFrom(to); setTo(from); }} className="p-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] transition-colors" data-testid="distance-swap" aria-label="swap">
              <ArrowRightLeft size={16} />
            </button>
          </div>
          <div>
            <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.to")}</label>
            <select value={to} onChange={(e) => setTo(e.target.value)} className="w-full font-mono-wr text-lg border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2 focus:border-[var(--wr-ink)] focus:outline-none" data-testid="distance-to">
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button type="submit" disabled={loading} className="w-full py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] font-mono-wr text-[11px] uppercase tracking-widest hover:bg-[var(--wr-red)] transition-colors disabled:opacity-50" data-testid="distance-submit">
            {loading ? t("common.loading") : t("common.calculate")}
          </button>
        </form>

        <div className="lg:col-span-7 wr-sharp-card p-8 lg:p-12 bg-[var(--wr-night)] text-[var(--wr-bg)] min-h-[360px]" data-testid="distance-result">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-white/75">{t("common.result")}</p>
          {result && !result.error && (
            <>
              <p className="font-editorial text-4xl lg:text-5xl mt-4 leading-none tracking-tight">
                {result.from} <span className="text-[var(--wr-red)]">→</span> {result.to}
              </p>
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6 font-mono-wr">
                <div>
                  <p className="text-white/75 text-[10px] uppercase tracking-widest mb-1">{locale === "ro" ? "Linie dreaptă" : "Straight-line"}</p>
                  <p className="text-4xl">{result.straight_km} <span className="text-lg text-white/80">km</span></p>
                </div>
                <div>
                  <p className="text-white/75 text-[10px] uppercase tracking-widest mb-1">{locale === "ro" ? "Rutier (estimat)" : "By road (estimate)"}</p>
                  <p className="text-4xl">{result.road_km_estimate} <span className="text-lg text-white/80">km</span></p>
                </div>
                <div>
                  <p className="text-white/75 text-[10px] uppercase tracking-widest mb-1">{locale === "ro" ? "Timp de condus" : "Drive time"}</p>
                  <p className="text-4xl">{result.drive_hours_estimate} <span className="text-lg text-white/80">h</span></p>
                </div>
                <div>
                  <p className="text-white/75 text-[10px] uppercase tracking-widest mb-1">{locale === "ro" ? "Combustibil" : "Fuel"}</p>
                  <p className="text-4xl">≈{result.fuel_cost_ron_estimate} <span className="text-lg text-white/80">RON</span></p>
                  <p className="text-white/70 text-xs mt-1">{result.fuel_liters_estimate} L · 7 L/100km</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </ToolShell>
  );
}
